import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Chip,
  Stack,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import DoneAllRounded from "@mui/icons-material/DoneAllRounded";
import RuleRounded from "@mui/icons-material/RuleRounded";
import SummarizeRounded from "@mui/icons-material/SummarizeRounded";
import ProjectCodeBlock from "./projectCode";
import { useError } from "../../Context/errorContext";
import MyHoursCompare from "../myHoursCompare";
import JoosepCompare from "./joosepCompare";
import { statuses } from "../helpers";

/**
 * A refined, accessible decision form with clearer hierarchy, guardrails, and nicer visuals.
 *
 * Notes:
 * - Keeps your prop API intact; can be used as a drop-in replacement.
 * - Disables submit until required inputs are valid (mirrors your required logic).
 * - Shows role, selection preview, and context about when comments are required.
 */
const ProjectDecisionForm = ({
  isAdmin,
  asAccountant,
  toggleAccountant,
  isManager,
  isAccountant,
  project,
  decision,
  handleCommentsChange,
  handleDecisionChange,
  handleSubmitDecision,
  comments,
  lastDecision,
  user,
  setDecisionByRole,

  fixedCode,
  serialNumber,
  handleChange,
  handleCodeValidation,
  handleCodeGeneration,
  handleCodeCancel,
  similarProjects,
  isCodeValid,
  validating,

  syncToMyHours,
  setSyncToMyHours,
  syncToJoosep,
  setSyncToJoosep,
}) => {
  const { setAlert } = useError();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const actingAsAccountant = isAdmin ? asAccountant : isAccountant;

  const showEditCode = useMemo(() => {
    return decision === "confirm";
  }, [decision]);

  const mustComment = useMemo(() => {
    // accountant confirms at status 5, manager approves at 3
    return decision === "review" || decision === "reject";
  }, [decision]);

  const decisionLabel = useMemo(() => {
    if (decision === "approve") return "Approve Project Application";
    if (decision === "review") return "Request Changes";
    if (decision === "confirm") return "Confirm Project Application";
    if (decision === "reject") return "Reject Project Application";
    return "";
  }, [decision]);

  const canSubmit = useMemo(() => {
    const hasChoice = decision !== null;
    const hasCommentsIfRequired =
      !mustComment || (comments && comments.trim().length > 0);
    return hasChoice && hasCommentsIfRequired && !loading;
  }, [decision, mustComment, comments, loading]);

  const handleConfirmedSubmit = async () => {
    setLoading(true);
    try {
      await handleSubmitDecision();
    } finally {
      setLoading(false);
      setConfirmDialogOpen(false);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const expectedApprover = useMemo(() => {
    if (project.status_id !== statuses.WAITING_FOR_UNIT_MANAGER_SIGNOFF)
      return null;
    const lastApprover =
      lastDecision?.decision === "Approved"
        ? project.approvers.find(
            (a) => a.approver_role === lastDecision.decision_by_role,
          )
        : null;
    const expectedApprover =
      lastApprover !== null
        ? project.approvers.find(
            (a) => a.approver_order === lastApprover.approver_order + 1,
          )
        : project.approvers.find((a) => a.approver_order === 0);

    return expectedApprover;
  }, [
    project.status_id,
    project.approvers,
    lastDecision?.decision,
    lastDecision?.decision_by_role,
  ]);

  useEffect(() => {
    setDecisionByRole(
      asAccountant
        ? "accountant"
        : (expectedApprover?.approver_role ?? "manager"),
    );
  }, [setDecisionByRole, asAccountant, expectedApprover]);

  const showForm =
    (isManager &&
      project.status_id === statuses.WAITING_FOR_UNIT_MANAGER_SIGNOFF &&
      (!expectedApprover ||
        expectedApprover.approver_email === user.userData.userName)) ||
    (isAccountant &&
      project.status_id === statuses.WAITING_FOR_ACCOUNTING_SIGNOFF) ||
    (isAdmin &&
      [
        statuses.IN_IDEA_REVIEW,
        statuses.IN_APPLICATION_REVIEW,
        statuses.WAITING_FOR_UNIT_MANAGER_SIGNOFF,
        statuses.WAITING_FOR_ACCOUNTING_SIGNOFF,
      ].includes(project.status_id));
  if (!showForm) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: (t) => t.palette.background.paper,
          maxWidth: 1200,
          mx: "auto",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Stack>
            <Typography variant="h6" fontWeight={600}>
              Submit {actingAsAccountant ? "Accounting" : "Manager"} Decision
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Project: <strong>{project?.name || "Untitled"}</strong>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              color={actingAsAccountant ? "secondary" : "primary"}
              variant="outlined"
              icon={<RuleRounded />}
              label={actingAsAccountant ? "Accounting" : "Manager"}
            />
          </Stack>
        </Stack>

        {/* Role toggle for Admins */}
        {isAdmin && project.status_id < statuses.CONFIRMED && (
          <Box sx={{ mt: 3 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Submit decision as</FormLabel>
              <RadioGroup
                row
                name="admin-decision-role"
                value={asAccountant ? "accountant" : "manager"}
                onChange={(e) => {
                  toggleAccountant(e.target.value === "accountant");
                  handleDecisionChange(null);
                }}
              >
                <FormControlLabel
                  value="manager"
                  control={<Radio />}
                  label="Manager"
                />
                <FormControlLabel
                  value="accountant"
                  control={<Radio />}
                  label="Accounting"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Decision form */}
        <Box
          component="form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (e.currentTarget.checkValidity()) {
              setConfirmDialogOpen(true);
            } else {
              e.currentTarget.reportValidity();
            }
          }}
        >
          <Stack spacing={2.5}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Decision</FormLabel>
              <RadioGroup
                aria-label="decision"
                name="decision"
                value={decision || ""}
                onChange={(e) => handleDecisionChange(e.target.value)}
              >
                <FormControlLabel
                  value={actingAsAccountant ? "confirm" : "approve"}
                  control={<Radio color="success" />}
                  label={
                    actingAsAccountant
                      ? "Confirm Project Application"
                      : "Approve Project Application"
                  }
                />
                <FormControlLabel
                  value={"review"}
                  control={<Radio color="warning" />}
                  label="Request Changes"
                />
                <FormControlLabel
                  value={"reject"}
                  control={<Radio color="error" />}
                  label="Reject Project Application"
                />
              </RadioGroup>
            </FormControl>

            {showEditCode && (
              <ProjectCodeBlock
                serialNumber={serialNumber}
                fixedCode={fixedCode}
                handleChange={handleChange}
                editCode={true}
                setEditCode={() => {}}
                handleCodeCancel={handleCodeCancel}
                similarProjects={similarProjects}
                project={project}
                handleCodeValidation={handleCodeValidation}
                handleCodeGeneration={handleCodeGeneration}
                decisionForm={true}
                isCodeValid={isCodeValid}
                validating={validating}
              ></ProjectCodeBlock>
            )}

            <TextField
              id="comments"
              label="Comments"
              placeholder={
                mustComment
                  ? "Explain your decision…"
                  : "Optional notes for the team"
              }
              required={mustComment}
              multiline
              rows={4}
              fullWidth
              value={comments || ""}
              onChange={handleCommentsChange}
              helperText={
                mustComment
                  ? "Comments are required for Request Changes or Reject."
                  : "Optional but appreciated for audit trail."
              }
            />

            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <Tooltip title="Review before sending">
                <span>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!canSubmit}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DoneAllRounded />
                      )
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    {loading ? "Submitting…" : "Submit Decision"}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {/* Confirmation dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="body"
        PaperProps={{
          sx: {
            maxHeight: "70vh", // limits dialog height
          },
        }}
      >
        <DialogTitle>Confirm your decision</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will notify project stakeholders and update the project record.
          </DialogContentText>
          <Stack spacing={1.5} paddingBottom={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SummarizeRounded fontSize="small" />
              <Typography variant="body2">
                <strong>Acting as:</strong>{" "}
                {actingAsAccountant ? "Accounting" : "Manager"}
              </Typography>
            </Stack>
            <Typography variant="body2">
              <strong>Project:</strong> {project.name || "—"}
            </Typography>
            {decision === "confirm" && (
              <Typography variant="body2">
                <strong>Code:</strong> {fixedCode + serialNumber || "—"}
              </Typography>
            )}
            <Typography variant="body2">
              <strong>Decision:</strong> {decisionLabel || "—"}
            </Typography>
            {comments?.trim() && (
              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Comments
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {comments}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Stack>
          {decision === "confirm" && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <MyHoursCompare
                newCode={fixedCode + serialNumber}
                project={project}
                syncToMyHours={syncToMyHours}
                setSyncToMyHours={setSyncToMyHours}
                setAlert={setAlert}
              ></MyHoursCompare>

              <Divider sx={{ py: 1, my: 2 }}></Divider>

              <JoosepCompare
                newCode={fixedCode + serialNumber}
                project={project}
                syncToJoosep={syncToJoosep}
                setSyncToJoosep={setSyncToJoosep}
                setAlert={setAlert}
              ></JoosepCompare>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1.5 }}>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false);
              setSyncToMyHours(true);
            }}
            color="secondary"
            variant="text"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmedSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? "Submitting…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDecisionForm;
